import * as React from 'react';
import { FormControl, Select, MenuItem, InputLabel, Typography } from '@material-ui/core';
import { graphql, useStaticQuery } from 'gatsby';
import { setGTMEvent } from '../utils/setGAEvent';
import { LocationLanguageContext } from '../contexts/LocationLanguageContext';
import { LocationLanguageContextType, FilterSort, StrapiFilterSort, FilterSortFieldsProps } from '../typings';
import FilterField from './FilterField';
import useStyles from '../styles/componentStyle';

const FilterSortFields: React.FC<FilterSortFieldsProps> = ({
  onFilterSortChange,
  isBrowsePage,
  filterSortObj,
  languages,
  categories,
  trades,
  isMobile
}) => {
  const data = useStaticQuery(
    graphql`
      query FilterSortQuery {
        allStrapiFilterSort {
          nodes {
            FilterLabel
            SortByLabel
            ClearAllLabel
            SelectAllLabel
            LanguageLabel
            CategoryLabel
            TradeLabel
            RelevanceLabel
            TitleAscLabel
            TitleDescLabel
            DateDescLabel
            DateAscLabel
            locale
          }
        }
      }
    `
  );

  const locationLanguage = React.useContext<LocationLanguageContextType | null>(LocationLanguageContext);
  const language: string = locationLanguage?.language ?? 'en';
  const filterSortContent: StrapiFilterSort = data.allStrapiFilterSort.nodes.find((x: any) => x.locale === language);
  const classes = useStyles();
  // Use the incoming prop directly so that external updates (e.g. language change) are reflected immediately.
  const filterSortValue = filterSortObj;

  const filtersDataObj = {
    language: {
      labelLocale: filterSortContent?.LanguageLabel,
      options: languages,
      selected: filterSortValue?.filters.language
    },
    trades: {
      labelLocale: filterSortContent?.TradeLabel,
      options: trades,
      selected: filterSortValue?.filters.trades
    },
    categories: {
      labelLocale: filterSortContent?.CategoryLabel,
      options: categories,
      selected: filterSortValue?.filters.categories
    }
  };

  const filterActions = {
    selectAll: { label: filterSortContent?.SelectAllLabel, value: '1' },
    clearAll: { label: filterSortContent?.ClearAllLabel, value: '0' }
  };

  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const updated: FilterSort = { filters: filterSortValue.filters, sort: event.target.value as string };
    onFilterSortChange(updated);
    setGTMEvent('sort', 'select_content', event.target.value);
  };

  const clearAll = () => {
    const cleared: FilterSort = {
      filters: { language: [], categories: [], trades: [] },
      sort: filterSortValue.sort
    };
    onFilterSortChange(cleared);
    setGTMEvent('filter.ClearAll', 'select_content', '');
  };

  /**
   * @description Update the value of filters when the filter checkbox is triggered
   */
  const handleFilterChange = (filterKey: string, filteredValue: string[]) => {
    const updated: FilterSort = {
      filters: { ...filterSortValue.filters, [filterKey]: filteredValue },
      sort: filterSortValue.sort
    };
    onFilterSortChange(updated);
  };

  /**
   * @description Check if any filter is selected on web
   */
  const selectedFiltersOnWeb: boolean =
    !isMobile &&
    (filterSortValue.filters.categories.length > 0 ||
      filterSortValue.filters.trades.length > 0 ||
      filterSortValue.filters.language.length > 0);

  // Effect removed: we now push changes upstream immediately upon user actions.

  if (!filterSortContent) return null;
  return (
    <>
      <div className={classes.filterTitleContainer}>
        <Typography variant='body1'>{filterSortContent.FilterLabel}</Typography>
        {selectedFiltersOnWeb ? (
          <Typography variant='body1' color='primary' className={classes.clearAllText} onClick={clearAll}>
            {filterSortContent.ClearAllLabel}
          </Typography>
        ) : null}
      </div>
      <br />
      {Object.entries(filtersDataObj).map(([filterKey, filterValue]) => (
        <FilterField
          key={filterKey}
          selectedFilters={filterValue.selected}
          filterOptions={filterValue.options || []}
          fieldLabel={filterValue.labelLocale}
          filterKey={filterKey}
          filterActions={filterActions}
          onFilterChange={handleFilterChange}
        />
      ))}
      <Typography variant='body1'>{filterSortContent.SortByLabel}</Typography>
      <br />
      <FormControl fullWidth variant='outlined'>
        <InputLabel style={{ zIndex: 0 }} id='sort-outlined-label'>
          {filterSortContent.SortByLabel}
        </InputLabel>
        <Select
          label={filterSortContent.SortByLabel}
          labelId='sort-outlined-label'
          value={filterSortValue.sort}
          onChange={handleSortChange}
        >
          {!isBrowsePage && (
            <MenuItem value='relevance' key='relevance'>
              {filterSortContent.RelevanceLabel}
            </MenuItem>
          )}
          <MenuItem value='titleasc' key='titleasc'>
            {filterSortContent.TitleAscLabel}
          </MenuItem>
          <MenuItem value='titledesc' key='titledesc'>
            {filterSortContent.TitleDescLabel}
          </MenuItem>
          <MenuItem value='datedesc' key='datedesc'>
            {filterSortContent.DateDescLabel}
          </MenuItem>
          <MenuItem value='dateasc' key='dateasc'>
            {filterSortContent.DateAscLabel}
          </MenuItem>
        </Select>
      </FormControl>
    </>
  );
};

export default FilterSortFields;
